import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  
  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [originalAdminToken, setOriginalAdminToken] = useState(null);
  const [originalAdminUser, setOriginalAdminUser] = useState(null);

  // Helper function to add role methods to user object
  const addRoleMethods = (userData) => {
    if (!userData) return userData;
    
    // Create a new object to ensure React detects changes
    const userWithMethods = { ...userData };
    userWithMethods.isAdmin = () => userWithMethods.role === 'admin';
    userWithMethods.isCoach = () => userWithMethods.role === 'coach';
    userWithMethods.hasAdminAccess = () => userWithMethods.role === 'admin' || userWithMethods.role === 'coach';
    userWithMethods.canEdit = () => userWithMethods.role === 'admin';
    
    return userWithMethods;
  };

  // Decode JWT exp claim (in ms) so the session can be ended proactively at expiry.
  // Returns null if the token can't be decoded.
  const getTokenExpiry = (authToken) => {
    try {
      const payload = JSON.parse(atob(authToken.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  };

  const checkAuthStatus = useCallback(async () => {
    try {
      console.log('🔐 AuthContext: Checking auth status...');
      console.log('🔐 AuthContext: Current token in state:', token);
      console.log('🔐 AuthContext: Token in localStorage:', localStorage.getItem('token'));
      
      const response = await axios.get('/api/auth/profile');
      const userData = response.data;
      
      setUser(addRoleMethods(userData));
      
      // If we have a user, we should also have a token in localStorage
      // Set the axios authorization header for future requests
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        console.log('🔐 AuthContext: Authorization header set from stored token');
        console.log('🔐 AuthContext: Axios header now:', axios.defaults.headers.common['Authorization']);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Perform a silent logout during background auth checks to avoid noisy toasts
      logout({ silent: true });
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Configure axios interceptor for authentication
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuthStatus();
    } else {
      setLoading(false);
    }
  }, [token, checkAuthStatus]);

  // Login user
  const login = async (userData, authToken) => {
    console.log('🔐 AuthContext: Login function called with:', { userData, authToken });
    try {
      if (userData && authToken) {
        // Direct login with user data and token (from password setup)
        console.log('🔐 AuthContext: Direct login with provided data...');
        localStorage.setItem('token', authToken);
        
        setUser(addRoleMethods(userData));
        setToken(authToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
        console.log('🔐 AuthContext: Direct login successful');
        return { success: true, user: userData, token: authToken };
      } else {
        // Traditional login with username/password
        console.log('🔐 AuthContext: Traditional login...');
        const response = await axios.post('/api/auth/login', userData);
        console.log('🔐 AuthContext: API response received:', response.data);
        
        const { token, user } = response.data;
        
        console.log('🔐 AuthContext: Setting token and user...');
        localStorage.setItem('token', token);
        
        setUser(addRoleMethods(user));
        setToken(token);
        
        // Set axios default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        console.log('🔐 AuthContext: Axios header set to:', axios.defaults.headers.common['Authorization']);
        console.log('🔐 AuthContext: Token stored in localStorage:', localStorage.getItem('token'));
        console.log('🔐 AuthContext: Current axios headers:', axios.defaults.headers.common);
        console.log('🔐 AuthContext: Returning success...');
        
        toast.success('Login successful!');
        return { success: true, user, token };
      }
    } catch (error) {
      console.error('🔐 AuthContext: Login error:', error);
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, message };
    }
  };


  const logout = (options = {}) => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    if (!options.silent) {
      toast.success('Logged out successfully');
    }
  };

  // Keep the latest logout() callable from stable interceptors/timers below.
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  // Global 401 handler: if any authenticated request returns 401 (invalid/expired
  // token, deactivated account, etc.), clear auth state so ProtectedRoute redirects
  // the user to /login instead of leaving them stranded on a stale session.
  useEffect(() => {
    const PUBLIC_AUTH_PATHS = [
      '/api/auth/login',
      '/api/auth/refresh',
      '/api/auth/setup-password',
      '/api/auth/forgot-password',
      '/api/auth/reset-password',
    ];

    const isPublicAuthRequest = (url = '') =>
      PUBLIC_AUTH_PATHS.some((path) => url.includes(path)) ||
      url.includes('/api/auth/verify-reset-token/') ||
      url.includes('/api/auth/verify-invitation/');

    let handlingUnauthorized = false;

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response?.status === 401 &&
          !isPublicAuthRequest(error.config?.url) &&
          !handlingUnauthorized
        ) {
          handlingUnauthorized = true;
          logoutRef.current({ silent: true });
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token]);

  // Proactive expiry: end the session at (just after) the token's actual expiry
  // time, so the user is taken to /login at the moment the session dies rather
  // than on their first failed action. Timers throttled in background tabs are
  // covered by the 401 interceptor above as a fallback.
  useEffect(() => {
    if (!token) return undefined;
    const expMs = getTokenExpiry(token);
    if (!expMs) return undefined;

    const timeoutId = setTimeout(() => {
      logoutRef.current({ silent: true });
    }, Math.max(expMs - Date.now() + 10000, 0));

    return () => clearTimeout(timeoutId);
  }, [token]);

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/api/auth/profile', profileData);
      // Update user data in context with the response
      const userData = response.data.user;
      
      setUser(addRoleMethods(userData));
      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
              await axios.put('/api/auth/change-password', { currentPassword, newPassword });
      toast.success('Password changed successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      return { success: false, message };
    }
  };

  const refreshToken = async () => {
    try {
      const response = await axios.post('/api/auth/refresh');
      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      
      setUser(addRoleMethods(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      return { success: true };
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Silent logout on refresh failure to avoid confusing toasts on public routes
      logout({ silent: true });
      return { success: false };
    }
  };

  const refreshUserData = async () => {
    try {
      const response = await axios.get('/api/auth/profile');
      const userData = response.data;
      
      setUser(addRoleMethods(userData));
      return { success: true };
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return { success: false };
    }
  };

  // Start impersonating a user (admin only)
  const startImpersonation = async (targetUserId) => {
    try {
      // Store original admin state
      const currentToken = localStorage.getItem('token');
      setOriginalAdminToken(currentToken);
      setOriginalAdminUser(user);

      // Call impersonation API
      const response = await axios.post(`/api/admin/users/${targetUserId}/impersonate`);
      const { token: impersonationToken, user: impersonatedUserData } = response.data;

      // Switch to impersonated user
      localStorage.setItem('token', impersonationToken);
      localStorage.setItem('originalAdminToken', currentToken);
      setToken(impersonationToken);
      setUser(addRoleMethods(impersonatedUserData));
      setImpersonatedUser(impersonatedUserData);
      setIsImpersonating(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${impersonationToken}`;

      toast.success(`Now viewing as ${impersonatedUserData.firstName} ${impersonatedUserData.lastName}`);
      return { success: true };
    } catch (error) {
      console.error('Impersonation failed:', error);
      const message = error.response?.data?.message || 'Failed to start impersonation';
      toast.error(message);
      return { success: false, message };
    }
  };

  // End impersonation and return to admin
  const endImpersonation = async () => {
    try {
      const adminToken = originalAdminToken || localStorage.getItem('originalAdminToken');
      
      if (!adminToken) {
        toast.error('Could not restore admin session');
        logout();
        return { success: false };
      }

      // Restore admin token
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('originalAdminToken');
      setToken(adminToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;

      // Fetch admin profile
      const response = await axios.get('/api/auth/profile');
      setUser(addRoleMethods(response.data));

      // Clear impersonation state
      setIsImpersonating(false);
      setImpersonatedUser(null);
      setOriginalAdminToken(null);
      setOriginalAdminUser(null);

      toast.success('Returned to admin session');
      return { success: true };
    } catch (error) {
      console.error('End impersonation failed:', error);
      toast.error('Failed to restore admin session');
      logout();
      return { success: false };
    }
  };

  // Check for impersonation state on load
  useEffect(() => {
    const storedOriginalToken = localStorage.getItem('originalAdminToken');
    if (storedOriginalToken) {
      setIsImpersonating(true);
      setOriginalAdminToken(storedOriginalToken);
    }
  }, []);

  const value = {
    user,
    loading,
    token,
    login,
    logout,
    updateProfile,
    changePassword,
    refreshToken,
    refreshUserData,
    isAuthenticated: !!user,
    isAdmin: user?.hasAdminAccess?.() || false,
    // Impersonation
    isImpersonating,
    impersonatedUser,
    startImpersonation,
    endImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

